import type { Node, Edge } from 'reactflow';
import { AlertTriangle, ArrowRight, CheckCircle2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { pendenciaDoNo, resumoDoNo } from '@shared/flow-node-summary';
import { pendenciaDoGatilho } from '@shared/flow-triggers';
import { acharNoDeEntrada } from '@shared/flow-utils';

export type PendenciaDoFluxo = {
  /** `null` quando o problema é do fluxo inteiro, não de um bloco. */
  noId: string | null;
  /** `true` quando o item é resolvido no painel do gatilho, não num bloco. */
  gatilho?: boolean;
  titulo: string;
  texto: string;
  /**
   * `true` impede salvar: o motor falharia nesse passo.
   * `false` é aviso — vale conferir, mas é normal no meio da montagem e não
   * pode travar quem está construindo o fluxo aos poucos.
   */
  bloqueia: boolean;
};

/**
 * Tudo que impede o fluxo de rodar, numa lista em que cada item leva ao bloco.
 *
 * Antes a validação disparava um toast por problema, espaçados 120ms, no canto
 * da tela: a pessoa lia "Nó 'Mensagem': texto vazio" sem saber qual dos quatro
 * blocos chamados "Mensagem" era, e os avisos sumiam sozinhos antes de ela
 * conseguir agir.
 */
export function listarPendencias(
  nome: string,
  nos: Node[],
  arestas: Edge[],
  gatilho?: {
    tipo: string | null | undefined;
    valor: string | null | undefined;
    /** `false` quando a etiqueta do gatilho não existe mais — ver `pendenciaDoGatilho`. */
    etiquetaExiste?: boolean;
  },
): PendenciaDoFluxo[] {
  const lista: PendenciaDoFluxo[] = [];

  // Gatilho incompleto é o mais caro de descobrir tarde: o fluxo fica ativo,
  // bonito, e simplesmente nunca dispara.
  if (gatilho) {
    const p = pendenciaDoGatilho(gatilho.tipo, gatilho.valor, gatilho.etiquetaExiste);
    if (p) {
      lista.push({
        noId: null,
        gatilho: true,
        // Gatilho descontinuado ou desconhecido é AVISO: travar o Salvar
        // impediria a pessoa de abrir o fluxo justamente para trocá-lo.
        bloqueia: p.bloqueia,
        titulo: `Começa quando: ${p.curta.toLowerCase()}`,
        texto: p.longa,
      });
    }
  }

  if (!nome.trim()) {
    lista.push({ noId: null, bloqueia: true, titulo: 'O fluxo precisa de um nome', texto: 'Dê um nome que você reconheça na lista de fluxos.' });
  }
  if (nos.length === 0) {
    lista.push({ noId: null, bloqueia: true, titulo: 'O fluxo está vazio', texto: 'Adicione o primeiro passo pela prateleira à esquerda.' });
    return lista;
  }

  for (const no of nos) {
    const p = pendenciaDoNo(no.type, no.data);
    if (p) {
      lista.push({
        noId: no.id,
        // `bloqueia: false` existe para o que a pessoa não consegue resolver
        // configurando (bloco descontinuado): travaria o Salvar do fluxo
        // inteiro sem saída.
        bloqueia: p.bloqueia ?? true,
        titulo: `${(no.data as { label?: string })?.label || rotuloPadrao(no.type)}: ${p.curta.toLowerCase()}`,
        texto: p.longa,
      });
    }
  }

  // Bloco que ninguém alcança nunca roda. Não impede de salvar, mas quase
  // sempre é uma aresta que a pessoa achou que tinha ligado.
  //
  // A entrada usa a MESMA regra do canvas e do espelhamento (`acharNoDeEntrada`).
  // Antes isto isentava `nos[0]` pelo índice do array: o nó de entrada real
  // aparecia como órfão se não fosse o primeiro da lista, e um órfão de
  // verdade no índice 0 passava batido.
  const temEntrada = new Set(arestas.map((a) => a.target));
  const idDaEntrada = acharNoDeEntrada(nos, arestas)?.id;
  const soltos = nos.filter((n) => n.id !== idDaEntrada && !temEntrada.has(n.id));
  for (const n of soltos) {
    lista.push({
      noId: n.id,
      // Aviso, não bloqueio: ligar tudo é o último gesto de quem monta um
      // fluxo, e travar o Salvar antes disso obrigaria a perder o rascunho.
      bloqueia: false,
      titulo: `${(n.data as { label?: string })?.label || rotuloPadrao(n.type)}: ninguém chega aqui`,
      texto: 'Nenhuma seta aponta para este bloco, então ele nunca vai rodar. Ligue-o a um passo anterior ou apague.',
    });
  }

  return lista;
}

function rotuloPadrao(tipo: string | undefined) {
  return (
    {
      content: 'Mensagem',
      action: 'Ação',
      condition: 'Condição',
      delay: 'Espera',
      randomizer: 'Divisão de tráfego',
      data_collection: 'Pergunta',
      ai_assistant: 'IA',
      ai_classifier: 'Classificador',
      webhook_request: 'Webhook',
      target: 'Público-Alvo',
      end: 'Fim',
    }[tipo ?? ''] ?? 'Bloco'
  );
}

export function PainelDePendencias({
  pendencias,
  nos,
  aoIrPara,
  aoAbrirGatilho,
  aoFechar,
}: {
  pendencias: PendenciaDoFluxo[];
  nos: Node[];
  aoIrPara: (noId: string) => void;
  aoAbrirGatilho: () => void;
  aoFechar: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b flex items-start justify-between gap-2 shrink-0">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">Antes de ativar</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {pendencias.length === 0 ? 'Nada pendente.' : 'Clique num item para ir até o bloco.'}
          </p>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground" onClick={aoFechar}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {pendencias.length === 0 ? (
          <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" strokeWidth={2} />
            <p className="text-xs text-emerald-800 leading-snug">
              Tudo certo. O fluxo já pode ser salvo e ativado.
            </p>
          </div>
        ) : (
          pendencias.map((p, i) => {
            const no = p.noId ? nos.find((n) => n.id === p.noId) : null;
            const clicavel = !!p.noId || !!p.gatilho;
            const cor = p.bloqueia
              ? { borda: 'border-amber-200', fundo: 'bg-amber-50/60', hover: 'hover:bg-amber-100/70', icone: 'text-amber-600', titulo: 'text-amber-900', texto: 'text-amber-800/80', resumo: 'text-amber-700/60' }
              : { borda: 'border-border', fundo: 'bg-muted/40', hover: 'hover:bg-muted/70', icone: 'text-muted-foreground', titulo: 'text-foreground', texto: 'text-muted-foreground', resumo: 'text-muted-foreground/70' };
            return (
              <button
                key={`${p.noId ?? 'fluxo'}-${i}`}
                type="button"
                disabled={!clicavel}
                onClick={() => (p.gatilho ? aoAbrirGatilho() : p.noId && aoIrPara(p.noId))}
                className={`w-full text-left rounded-lg border p-2.5 transition-colors ${cor.borda} ${cor.fundo} ${
                  clicavel ? `${cor.hover} cursor-pointer` : 'cursor-default'
                }`}
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${cor.icone}`} strokeWidth={2} />
                  <div className="min-w-0 flex-1">
                    <div className={`text-xs font-medium ${cor.titulo}`}>{p.titulo}</div>
                    <p className={`text-[11px] leading-snug mt-0.5 ${cor.texto}`}>{p.texto}</p>
                    {no && (
                      <p className={`text-[10px] mt-1 truncate ${cor.resumo}`}>{resumoDoNo(no.type, no.data)}</p>
                    )}
                  </div>
                  {clicavel && <ArrowRight className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${cor.icone}`} />}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
